import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/Button";
import { api, type DailyTask } from "@/lib/api";
import { isAuthenticated } from "@/lib/pb";

export const Route = createFileRoute("/daily/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: DailyPage,
});

const PRIORITY_STYLE: Record<string, string> = {
  urgent: "text-danger border-danger/40",
  high: "text-warn border-warn/40",
  normal: "text-muted border-border",
  low: "text-muted/60 border-border",
};

function DailyPage() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["daily"],
    queryFn: () => api.daily.read(),
  });

  const refresh = useMutation({
    mutationFn: () => api.daily.refresh(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["daily"] }),
  });

  if (q.isLoading) {
    return (
      <Shell>
        <div className="surface p-12 text-center">
          <p className="font-mono text-sm text-muted">$ loading briefing…</p>
        </div>
      </Shell>
    );
  }

  const b = q.data;

  if (!b?.available) {
    return (
      <Shell>
        <Header onRefresh={() => refresh.mutate()} pending={refresh.isPending} />
        <div className="surface p-8 space-y-3">
          <p className="font-mono text-sm text-danger">{b?.error ?? "sin briefing"}</p>
          <p className="text-sm text-muted">
            El job de las 07:00 no ha corrido todavía, o falta el token de ClickUp en el
            backend. Podés generarlo ahora con el botón de arriba.
          </p>
        </div>
      </Shell>
    );
  }

  const plan = [...(b.overdue ?? []), ...(b.focus ?? [])];
  // null = este usuario no tiene plan de horas. Es distinto de 0 (descanso),
  // y mostrar una barra vacía en ese caso mentiría diciendo "no te queda nada".
  const capacity = b.capacity_hours ?? null;
  const committed = b.committed_hours ?? 0;
  const over = capacity !== null && capacity > 0 && committed > capacity;

  return (
    <Shell>
      <Header
        onRefresh={() => refresh.mutate()}
        pending={refresh.isPending}
        day={b.day}
        who={b.user_email}
      />

      {b.stale && (
        <div className="surface border-warn/40 p-4">
          <p className="font-mono text-sm text-warn">
            ⚠ Este briefing es del {b.day}, no de hoy. El job de las 07:00 no corrió.
          </p>
        </div>
      )}

      {/* Capacidad */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="surface p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <p className="font-mono text-xs text-muted uppercase tracking-wide">carga del día</p>
          <p className="font-mono text-sm">
            <span className={over ? "text-danger" : "text-text"}>{committed} h</span>
            {capacity !== null && <span className="text-muted"> / {capacity} h</span>}
          </p>
        </div>
        {capacity !== null && (
          <div className="mt-3 h-2 rounded-full bg-bg-elev overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${over ? "bg-danger" : "bg-primary"}`}
              style={{
                width: capacity > 0 ? `${Math.min(100, (committed / capacity) * 100)}%` : "0%",
              }}
            />
          </div>
        )}
        {(b.warnings ?? []).map((w, i) => (
          <p key={i} className="mt-2 text-sm text-warn">
            · {w}
          </p>
        ))}
      </motion.div>

      {/* El plan */}
      <Section title="Hoy" count={plan.length} hint="ordenado por lo que destraba, no por prioridad">
        {plan.length === 0 ? (
          <Empty>Nada accionable hoy. Revisá "Bloqueadas" abajo.</Empty>
        ) : (
          <ul className="space-y-2">
            {plan.map((t) => (
              <TaskRow key={t.id} t={t} showFit />
            ))}
          </ul>
        )}
      </Section>

      {/* Bloqueadas */}
      {(b.blocked ?? []).length > 0 && (
        <Section
          title="Bloqueadas"
          count={b.blocked!.length}
          hint="no se pueden empezar — falta algo antes"
        >
          <ul className="space-y-2">
            {b.blocked!.map((t) => (
              <TaskRow key={t.id} t={t} />
            ))}
          </ul>
        </Section>
      )}

      {/* Esperando a terceros */}
      {(b.waiting_on_others ?? []).length > 0 && (
        <Section
          title="Esperando"
          count={b.waiting_on_others!.length}
          hint="reloj ajeno — visible, no accionable"
        >
          <ul className="space-y-2">
            {b.waiting_on_others!.map((t) => (
              <TaskRow key={t.id} t={t} />
            ))}
          </ul>
        </Section>
      )}

      <p className="font-mono text-xs text-muted/60 text-center pt-2">
        {b.stats?.open ?? 0} abiertas · {b.stats?.actionable ?? 0} accionables ·{" "}
        {b.stats?.blocked ?? 0} bloqueadas · {b.stats?.waiting ?? 0} esperando
      </p>
    </Shell>
  );
}

function TaskRow({ t, showFit = false }: { t: DailyTask; showFit?: boolean }) {
  const overdue = !!t.due && new Date(t.due) < new Date(new Date().toDateString());

  return (
    <li
      className={`surface p-4 ${showFit && t.fits === false ? "opacity-50" : ""}`}
      title={showFit && t.fits === false ? "No entra en la capacidad de hoy" : undefined}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <a
            href={t.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm hover:text-primary transition-colors"
          >
            {t.name}
          </a>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap font-mono text-xs text-muted">
            <span>{t.folder ?? "—"}</span>
            <span className="text-muted/40">/</span>
            <span>{t.list ?? "—"}</span>
            {t.hours > 0 && <span className="text-muted/60">· {t.hours} h</span>}
            {t.energy && <span className="text-muted/60">· {t.energy}</span>}
            {t.block_type && <span className="text-violet">· {t.block_type}</span>}
          </div>

          {/* Por qué está acá arriba */}
          {!!t.unblocks && t.unblocks > 0 && (
            <p className="mt-1.5 font-mono text-xs text-primary">
              ↳ destraba {t.unblocks} tarea{t.unblocks > 1 ? "s" : ""}
            </p>
          )}

          {/* Qué falta antes */}
          {(t.blocked_by ?? []).length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {t.blocked_by!.map((d) => (
                <li key={d.id} className="font-mono text-xs text-warn">
                  ⟵ espera a{" "}
                  {d.url ? (
                    <a href={d.url} target="_blank" rel="noreferrer" className="underline">
                      {d.name}
                    </a>
                  ) : (
                    d.name
                  )}
                </li>
              ))}
            </ul>
          )}

          {t.owner && <p className="mt-1.5 font-mono text-xs text-muted">· {t.owner}</p>}
          {t.waiting_on && <p className="mt-1 font-mono text-xs text-muted">· {t.waiting_on}</p>}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {t.unassigned && (
            <span
              className="font-mono text-xs px-2 py-0.5 rounded border border-violet/40 text-violet"
              title="Nadie la tomó en ClickUp — sale en el tablero de los dos"
            >
              sin dueño
            </span>
          )}
          {overdue && (
            <span className="font-mono text-xs px-2 py-0.5 rounded border border-danger/40 text-danger">
              vencida
            </span>
          )}
          {t.priority && (
            <span
              className={`font-mono text-xs px-2 py-0.5 rounded border ${
                PRIORITY_STYLE[t.priority] ?? PRIORITY_STYLE.normal
              }`}
            >
              {t.priority}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function Header({
  onRefresh,
  pending,
  day,
  who,
}: {
  onRefresh: () => void;
  pending: boolean;
  day?: string;
  who?: string;
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
      <p className="path-chip">/daily · ranked by what it unblocks, not by priority</p>
      <div className="flex items-baseline justify-between gap-3 mt-1 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-3xl font-semibold">Daily Tasks</h1>
          {day && <span className="font-mono text-sm text-muted">[{day}]</span>}
          {/* Quién es el dueño de este tablero. Con dos personas usando el
              mismo Console, no decirlo invita a leer el día del otro. */}
          {who && <span className="font-mono text-xs text-muted/60">{who}</span>}
        </div>
        <Button onClick={onRefresh} disabled={pending}>
          {pending ? "recalculando…" : "recalcular"}
        </Button>
      </div>
    </motion.div>
  );
}

function Section({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className="font-mono text-sm text-muted">[{count}]</span>
        {hint && <span className="font-mono text-xs text-muted/60">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="surface p-8 text-center">
      <p className="font-mono text-sm text-muted">{children}</p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-6xl mx-auto space-y-4">{children}</div>;
}
